import { useState } from "react";
import { SignUp } from "@clerk/react";
import { Heart, LifeBuoy, ArrowRight, ArrowLeft, UserPlus, LogIn } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLocation } from "wouter";
import { safeGetItem, safeSetItem } from "@/lib/safeStorage";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const ROLE_META = {
  donor: {
    label: "Donor",
    description: "You're joining as a Donor — you'll be able to browse pools and donate to help people get debt-free.",
    color: "emerald",
    icon: <Heart className="w-5 h-5 text-emerald-600" />,
    borderClass: "border-emerald-200 bg-emerald-50/50",
    pillClass: "bg-emerald-100 text-emerald-800 border-emerald-300",
  },
  debtor: {
    label: "Debt Relief Applicant",
    description: "You're joining to apply for debt relief — you'll submit your debt documents for review and pool matching.",
    color: "blue",
    icon: <LifeBuoy className="w-5 h-5 text-blue-600" />,
    borderClass: "border-blue-200 bg-blue-50/50",
    pillClass: "bg-blue-100 text-blue-800 border-blue-300",
  },
};

export default function SignUpGateway() {
  const [location, navigate] = useLocation();
  const [pendingRole] = useState<"donor" | "debtor" | null>(() => {
    const r = safeGetItem("pendingRole");
    return r === "donor" || r === "debtor" ? r : null;
  });
  const [roleChosen, setRoleChosen] = useState<"donor" | "debtor" | null>(pendingRole);
  const [showClerkFormState, setShowClerkFormState] = useState(false);

  // Clerk path-mode routes to sub-paths like /sign-up/verify-email-address during
  // multi-step signup. Detect these so we never interrupt Clerk mid-flow — even if
  // this component remounts and local state resets.
  const isClerkSubRoute = /^\/sign-up\/.+/.test(location);
  const showClerkForm = isClerkSubRoute || showClerkFormState;

  function chooseRole(role: "donor" | "debtor") {
    safeSetItem("pendingRole", role);
    setRoleChosen(role);
    setShowClerkFormState(false);
  }

  if (isClerkSubRoute || (showClerkForm && roleChosen)) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
          <div className="max-w-4xl mx-auto px-4 h-14 flex items-center">
            {!isClerkSubRoute && (
              <button
                onClick={() => setShowClerkFormState(false)}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer p-0 font-inherit"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center">
          <SignUp
            routing="path"
            path={`${BASE}/sign-up`}
            signInUrl={`${BASE}/sign-in`}
            fallbackRedirectUrl={`${BASE}/select-role`}
          />
        </div>
      </div>
    );
  }

  if (roleChosen) {
    const meta = ROLE_META[roleChosen];
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-border/40">
          <button
            onClick={() => { setRoleChosen(null); setShowClerkFormState(false); }}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer p-0 font-inherit"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <Logo iconSize={32} />
          <div className="w-24" />
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className={`rounded-2xl border p-5 mb-8 flex items-start gap-3 ${meta.borderClass}`}>
              <div className="mt-0.5 shrink-0">{meta.icon}</div>
              <div>
                <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border mb-1.5 ${meta.pillClass}`}>
                  Joining as {meta.label}
                </span>
                <p className="text-sm text-foreground/80 leading-relaxed">{meta.description}</p>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-foreground mb-2 text-center">
              Welcome to DebtBridge
            </h1>
            <p className="text-muted-foreground text-center mb-8">
              Create a new account or sign in to an existing one.
            </p>

            <div className="flex flex-col gap-4">
              <button
                onClick={() => setShowClerkFormState(true)}
                className="w-full flex items-center gap-4 rounded-2xl border-2 border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 transition-all p-5 text-left cursor-pointer"
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">Create a new account</div>
                  <div className="text-sm text-muted-foreground">First time here — sign up to get started</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
              </button>

              <a
                href={`${BASE}/sign-in`}
                className="w-full flex items-center gap-4 rounded-2xl border-2 border-border hover:border-foreground/20 hover:bg-muted/50 transition-all p-5 text-left no-underline"
              >
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <LogIn className="h-5 w-5 text-foreground/70" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">Sign in to existing account</div>
                  <div className="text-sm text-muted-foreground">Already have a DebtBridge account</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
              </a>
            </div>

            <p className="mt-6 text-xs text-muted-foreground text-center max-w-sm mx-auto">
              By continuing, you agree to our{" "}
              <a href="/terms" className="underline hover:text-foreground">Terms of Service</a>{" "}
              and{" "}
              <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border/40">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer p-0 font-inherit"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </button>
        <Logo iconSize={32} />
        <div className="w-24" />
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full text-center mb-10">
          <h1 className="text-3xl font-bold text-foreground mb-3">
            How are you joining DebtBridge?
          </h1>
          <p className="text-muted-foreground text-lg">
            Choose your role — this cannot be changed later.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-2xl">
          <RoleCard
            icon={<Heart className="w-10 h-10 text-emerald-600" />}
            title="I want to Donate"
            subtitle="Help others become debt-free"
            features={[
              "Choose from debt relief pools",
              "Track your real-world impact",
              "Receive IRS tax deduction receipts",
              "Earn donor tier badges",
            ]}
            accentClass="border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50/40"
            buttonClass="bg-emerald-600 hover:bg-emerald-700 text-white"
            onSelect={() => chooseRole("donor")}
          />

          <RoleCard
            icon={<LifeBuoy className="w-10 h-10 text-blue-600" />}
            title="I need Debt Relief"
            subtitle="Submit your debt for community funding"
            features={[
              "Submit verified debt documents",
              "Get matched to a relief pool",
              "Track your application status",
              "Payment sent directly to creditor",
            ]}
            accentClass="border-blue-300 hover:border-blue-500 hover:bg-blue-50/40"
            buttonClass="bg-blue-600 hover:bg-blue-700 text-white"
            onSelect={() => chooseRole("debtor")}
          />
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          Already have an account?{" "}
          <a
            href={`${BASE}/sign-in`}
            className="underline hover:text-foreground font-medium"
          >
            Sign in
          </a>
        </p>

        <p className="mt-3 text-xs text-muted-foreground text-center max-w-sm">
          By continuing, you agree to our{" "}
          <a href="/terms" className="underline hover:text-foreground">Terms of Service</a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

interface RoleCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  features: string[];
  accentClass: string;
  buttonClass: string;
  onSelect: () => void;
}

function RoleCard({ icon, title, subtitle, features, accentClass, buttonClass, onSelect }: RoleCardProps) {
  return (
    <div
      className={`relative bg-card border-2 rounded-2xl p-8 flex flex-col gap-5 transition-all duration-200 cursor-pointer ${accentClass}`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-4">
        <div className="p-2 rounded-xl bg-background shadow-sm">{icon}</div>
        <div>
          <h2 className="text-xl font-bold text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      <ul className="flex flex-col gap-2">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-foreground">
            <span className="mt-0.5 text-emerald-500 font-bold">✓</span>
            {f}
          </li>
        ))}
      </ul>

      <button
        className={`mt-auto flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-colors ${buttonClass}`}
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
      >
        {title} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
